import React, { JSX } from "react";
import PrintIcon from "@mui/icons-material/Print";
import { Box, Tooltip } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import moment from "moment-timezone";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { getDisplayAgeFromDOB } from "../../../helpers/formatters";
import { LabClient, LabTestRecordResultData } from "../../../interfaces/Lab";
import mixpanelAnalytics from "../../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../../mixpanel-analytics/event";
import { PrintTypography } from "../../Stock/StockCreateEdit/BarCodePrint";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";

const sampleType = {
  serum: 1,
  urine: 2
};

const testProfileMappings = {
  lft: 1111,
  rft: 1112,
  lipid: 1113,
  iron: 1115
};

const barCodePrintPageStyle = `
  @page { 
    size: 50mm 25mm; 
    margin: 0; 
  }
  @media print {
    .page-break {
      page-break-after: always !important;
      break-after: page !important;
    }
  }
`;

interface PrintLabBarCodeInterface {
  labTests: LabTestRecordResultData[];
  client: LabClient;
  testDate: string;
}

const PrintLabBarCode: React.FC<PrintLabBarCodeInterface> = ({
  labTests,
  client,
  testDate
}): JSX.Element | null => {
  const currentDateAndTime = moment().format();
  const rc = useCurrentResourceCentre();
  const BSDate = convertADtoBS(moment.utc(currentDateAndTime.slice(0, 10))).formatted;
  const printRef = React.useRef(null);
  const print = useReactToPrint({
    content: () => printRef.current,
    pageStyle: barCodePrintPageStyle,
    onBeforePrint: () => {
      mixpanelAnalytics.track(EVENT.PRINT_LAB_BARCODE, { rcId: rc.id, rcName: rc.name });
    }
  });

  const sampleMap = new Map();

  // eslint-disable-next-line no-restricted-syntax
  for (const item of labTests) {
    const sn = item.sampleNumber;
    if (sn) {
      if (sampleMap.has(sn)) {
        // If sample number already exists, add the test name to existing names
        const existing = sampleMap.get(sn);
        if (item.name && !existing.names.includes(item.name)) {
          existing.names.push(item.name);
          existing.combinedNames = existing.names.join(", ");
        }
      } else {
        // New sample number
        sampleMap.set(sn, {
          sampleNumber: sn,
          names: item.name ? [item.name] : [],
          combinedNames: item.name || ""
        });
      }
    }
  }

  const uniqueSampleData = Array.from(sampleMap.values());

  const getFullName = (c: Partial<LabClient>): string => `${c?.firstName} ${c?.lastName}`;
  const fullName = getFullName(client);
  if (labTests.length === 0) return null;
  const gender = (g) => {
    if (g === "1") return "M";
    if (g === "2") return "F";
    return "";
  };

  return (
    <Box>
      <Tooltip title="Barcode print" aria-label="barcodeprint">
        <PrintIcon
          onClick={print}
          data-testomation="printButton"
          sx={{ cursor: "pointer", marginLeft: "10px", marginTop: "5px" }}
        />
      </Tooltip>

      <Box
        sx={{
          display: "none",
          "@media print": {
            display: "flex !important",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }
        }}
        ref={printRef}
      >
        {uniqueSampleData.map((item, index) => {
          const profileName = Object.keys(testProfileMappings).find((key) =>
            item.combinedNames.toLowerCase().includes(key)
          );

          const profileNumber = profileName ? testProfileMappings[profileName] : 0;
          return (
            <Box
              key={item.sampleNumber}
              display="flex"
              flexDirection="column"
              className={index !== uniqueSampleData.length - 1 ? "page-break" : undefined}
              sx={{
                "@media print": {
                  pageBreakAfter: "always",
                  breakAfter: "page"
                }
              }}
            >
              <PrintTypography
                text={`${fullName}${(client.dob || client.gender || "") && ", "}
          ${client.dob ? `${getDisplayAgeFromDOB(client.dob)}` : ""}
          ${client.dob && client.gender ? "/" : ""}
          ${client.gender ? gender(client.gender) : ""}`}
                fontSize="1.8mm"
                maxWidth="45mm"
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Barcode
                  value={`0${moment(testDate).format("YYYYMMDD")}${item.sampleNumber}${
                    sampleType.urine
                  }${profileNumber}`}
                  width={1}
                  displayValue={false}
                  height={20}
                  margin={0}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                <PrintTypography
                  text={`0${moment(testDate).format("YYYYMMDD")}${item.sampleNumber}${
                    sampleType.urine
                  }${profileNumber}`}
                />
                <PrintTypography text={BSDate} />
              </Box>
              <PrintTypography fontSize="1.7mm" maxWidth="37mm" text={item.combinedNames} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PrintLabBarCode;
